// Omnix/client/ui/ContactDetailView.js
import { ComponentBase } from "../core/ComponentBase.js";
import { createAvatarElement } from "../../helpers/avatarHelper.js";

export class ContactDetailView extends ComponentBase {
    static styles = `
        .contact-detail-view { padding: 1.5rem; height: 100%; overflow-y: auto; }
        .detail-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 2rem; }
        .detail-header h3 { margin: 0; }
        .detail-section { margin-bottom: 2rem; }
        .detail-section-title { font-weight: 600; color: var(--bs-secondary-color); margin-bottom: 1rem; font-size: 0.9rem; text-transform: uppercase; border-bottom: 1px solid var(--bs-border-color); padding-bottom: 0.5rem; }
        .detail-property { display: flex; flex-direction: column; margin-bottom: 1rem; }
        .detail-property-label { font-size: 0.8rem; color: var(--bs-secondary-color); }
        .detail-property-value { font-weight: 500; }
        .channel-item { display: flex; align-items: center; gap: 0.75rem; padding: 0.5rem 0; }
        .channel-icon { font-size: 1.2rem; color: var(--bs-primary); }
        .channel-info { flex-grow: 1; }
        .channel-value { font-weight: 500; }
        .channel-label { font-size: 0.8rem; color: var(--bs-secondary-color); }
        .placeholder { display: flex; flex-direction: column; justify-content: center; align-items: center; height: 100%; color: var(--bs-heading-color); background-color: var(--bs-secondary-bg-color) }
        .placeholder i { font-size: 4rem; margin-bottom: 1rem; }
        .mobile-back-btn { display: none; }
        @media (max-width: 768px) { .mobile-back-btn { display: inline-block; margin-right: 1rem; } }
    `;

    constructor(config) {
        super(config);
        this.contactData = null;
        this.allUsers = [];
        this.applyStyles();
    }

    setContact(contact) {
        this.contactData = contact;
        if (this.container) this.render();
    }

    setAllUsers(users) {
        this.allUsers = users;
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'contact-detail-view';
        }
        this.container.innerHTML = '';

        if (!this.contactData) {
            this.container.innerHTML = `<div class="placeholder"><i class="bi bi-person-vcard"></i><h4>Select a contact to view details</h4></div>`;
            return this.container;
        }

        const displayName = `${this.contactData.firstName || ''} ${this.contactData.lastName || ''}`.trim() || this.contactData.email || 'Unnamed Contact';
        const avatar = createAvatarElement({ displayName, sizeClass: 'medium' });
        const creator = this.allUsers.find(u => u._id === this.contactData.createdBy);

        const channelIcons = { 
            email: 'bi-envelope-fill', 
            sms: 'bi-chat-dots-fill', 
            whatsapp: 'bi-whatsapp', 
            voice: 'bi-telephone-fill', 
            web_chat: 'bi-globe',
            facebook_messenger: 'bi-messenger',
            instagram: 'bi-instagram',
            telegram: 'bi-telegram'
        };

        this.container.innerHTML = `
            <div class="detail-header">
                <button class="btn btn-link mobile-back-btn p-0 fs-4"><i class="bi bi-arrow-left"></i></button>
                ${avatar.outerHTML}
                <div>
                    <h3>${displayName}</h3>
                    <p class="text-muted mb-0">${this.contactData.jobTitle || this.contactData.company || ''}</p>
                </div>
                <div class="ms-auto btn-group btn-group-sm gap-2">
                    <button class="btn btn-outline-secondary" id="edit-contact-btn"><i class="bi bi-pencil"></i> Edit</button>
                    <button class="btn btn-outline-secondary" id="merge-contact-btn"><i class="bi bi-union"></i> Merge</button>
                    <button class="btn btn-outline-danger" id="delete-contact-btn"><i class="bi bi-trash"></i> Delete</button>
                </div>
            </div>

            <div class="detail-section">
                <h6 class="detail-section-title">Contact Details</h6>
                <div class="row">
                    <div class="col-md-6"><div class="detail-property"><span class="detail-property-label">Email</span><span class="detail-property-value">${this.contactData.email || '-'}</span></div></div>
                    <div class="col-md-6"><div class="detail-property"><span class="detail-property-label">Company</span><span class="detail-property-value">${this.contactData.company || '-'}</span></div></div>
                </div>
            </div>

            <div class="detail-section">
                <h6 class="detail-section-title">Tags</h6>
                <div id="contact-tags-display"></div>
            </div>

            <div class="detail-section">
                <h6 class="detail-section-title">Channels</h6>
                <div id="channels-list">
                    ${this.contactData.channels.map(ch => `
                        <div class="channel-item">
                            <i class="bi ${channelIcons[ch.type] || 'bi-question-circle'} channel-icon"></i>
                            <div class="channel-info">
                                <div class="channel-value">${ch.value} ${ch.isPreferred ? '<i class="bi bi-star-fill text-warning ms-1" title="Preferred"></i>' : ''}</div>
                                <div class="channel-label">${ch.label || this.t(ch.type)}</div>
                            </div>
                        </div>
                    `).join('') || `<p class="text-muted small">${this.t('No channels added.')}</p>`}
                </div>
            </div>

            <div class="detail-section">
                <h6 class="detail-section-title">Admin</h6>
                <div class="row">
                    <div class="col-md-6"><div class="detail-property"><span class="detail-property-label">Visibility</span><span class="detail-property-value">${this.t(this.contactData.visibility)}</span></div></div>
                    <div class="col-md-6"><div class="detail-property"><span class="detail-property-label">Created By</span><span class="detail-property-value">${creator?.username || this.contactData.createdBy}</span></div></div>
                </div>
            </div>
        `;

        const tagsContainer = this.container.querySelector('#contact-tags-display');
        if (this.contactData.tags && this.contactData.tags.length > 0) {
            tagsContainer.innerHTML = this.contactData.tags.map(tag => 
                `<span class="badge bg-secondary me-1">${tag}</span>`
            ).join('');
        } else {
            tagsContainer.innerHTML = `<p class="text-muted small">${this.t('No tags added.')}</p>`;
        }

        this.container.querySelector('#edit-contact-btn').addEventListener('click', () => this.emit('editContactClicked', this.contactData));
        this.container.querySelector('#merge-contact-btn').addEventListener('click', () => this.emit('mergeContactClicked', this.contactData));
        this.container.querySelector('#delete-contact-btn').addEventListener('click', () => this.emit('deleteContactClicked', this.contactData));
        this.container.querySelector('.mobile-back-btn').addEventListener('click', () => this.emit('closeDetail'));
        return this.container;
    }
}